<template>
  <main-large-dialog
    v-bind="$props"
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t('dialog.title.curveDetails')"
    no-paddings-y
    no-paddings-x
    dialog-width="70%"
    max-height="90vh"
    bodyHeight="70vh"
    header-shadow
    agree-text="dialog.agreeSave"
    @modalClosed="onClose"
    @agree="handleCurve"
    :loading="isLoading"
    :is-valid="isValid"
  >
    <template #body>
      <v-row no-gutters class="fill-height">
        <v-col cols="4" class="fill-height">
          <v-row no-gutters style="align-content: space-between" class="pt-5">
            <main-input-dialog-row
              v-for="input in inputs"
              v-bind="input"
              :key="input.name"
              class="px-5"
              @change="changeValue($event)"
            >
              <template #name.input>
                <v-col cols="6">
                  <data-input v-model="payload.name" v-on="$listeners" v-bind="input"></data-input>
                </v-col>
              </template>
              <template #description.input>
                <v-col cols="6">
                  <data-input v-model="payload.description" v-on="$listeners" v-bind="input"></data-input>
                </v-col>
              </template>
              <template #curve_type.input>
                <v-col cols="6">
                  <data-input v-model="payload.curve_type" v-bind="input"></data-input>
                </v-col>
              </template>
            </main-input-dialog-row>
          </v-row>
          <v-row no-gutters style="overflow-y: auto; height: 60%">
            <v-col>
              <v-row
                no-gutters
                class="tableHeader py-3"
                style="position: sticky; top: 0; background-color: white; z-index: 2"
              >
                <v-col cols="5">{{ leftColumnTitle }}</v-col>
                <v-col cols="5">{{ rightColumnTitle }}</v-col>
              </v-row>
              <v-row no-gutters class="tableRow py-3" v-for="(line, index) in chartData" :key="index">
                <v-col cols="5">{{ line[0] }}</v-col>
                <v-col cols="5">{{ line[1] }}</v-col>
                <v-col cols="2">
                  <v-icon style="'pointer'" color="error" @click="handlePointDelete(index)">
                    mdi-close
                  </v-icon>
                </v-col>
              </v-row>
              <v-row no-gutters class="tableRow" align="center" justify="space-between">
                <v-col cols="4" class="pa-2 pl-8">
                  <data-input
                    v-model="newPoint.firstValue"
                    :data-type="dataType"
                    :allow-negative="false"
                    hide-details
                  ></data-input>
                </v-col>

                <v-col cols="4" class="pa-2 pl-8">
                  <data-input
                    v-model="newPoint.secondValue"
                    :data-type="dataType"
                    :allow-negative="false"
                    hide-details
                  ></data-input>
                </v-col>
                <v-col cols="2" align-self="center">
                  <v-icon
                    :style="`cursor: ${isNewPointValid ? 'pointer' : 'default'}`"
                    :color="isNewPointValid ? 'success' : 'grey'"
                    :disabled="!isNewPointValid"
                    @click="handlePointAdd"
                    >mdi-check</v-icon
                  >
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="8" class="fill-height chart">
          <canvas id="curveChart" v-show="isChartVisible" class="pl-6 pt-10"></canvas>
        </v-col>
      </v-row>
    </template>
  </main-large-dialog>
</template>

<script>
import regression from 'regression';
import { call, get } from 'vuex-pathify';
import { cloneDeep } from 'lodash';
import Chart from 'chart.js';

import MainInputDialogRow from '@/components/MainInputDialogRow';
import DataInput from '@/components/DataInput';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'ModelingCurveDialog',
  components: { MainInputDialogRow, MainLargeDialog, DataInput },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    curve: {
      type: Object,
      required: true,
    },
  },
  data: vm => ({
    newPoint: {},
    dataType: {
      name: 'float',
      rules: [v => !!v || 'rules.required'],
    },
    inputRules: {
      required: v => !!v || 'rules.required',
      noWhitespaces: v => {
        v === null ? (v = '') : v;
        return !!v.match(/^\S*$/) || 'rules.notAllowedWhitespace';
      },
    },
    curveTypes: [
      {
        text: 'PUMP',
        value: 'PUMP',
      },
      {
        text: 'HEADLOSS',
        value: 'HEADLOSS',
      },
      {
        text: 'VOLUME',
        value: 'VOLUME',
      },
      {
        text: 'EFFICIENCY',
        value: 'EFFICIENCY',
      },
    ],
    payload: {
      name: vm.curve.name || '',
      description: vm.curve.description || '',
      curve_type: vm.curve.curve_type || '',
      data: vm.curve.data || [],
    },
    isChartVisible: true,
    isLoading: false,
    chartData: [],
    chart: undefined,
  }),
  computed: {
    curves: get('epanet/curves'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    nameAlreadyUsed() {
      return this.curves
        .filter(curve => curve.name !== this.curve.name)
        .some(curve => curve.name === this.payload.name);
    },
    isNewPointValid() {
      return !(
        this.newPoint.firstValue === null ||
        this.newPoint.firstValue === undefined ||
        this.newPoint.secondValue === null ||
        this.newPoint.secondValue === undefined
      );
    },
    isValid() {
      if (this.chartData.length === 0) {
        return false;
      }
      return !!(this.payload.name && this.payload.curve_type && this.payload.name.match(/^\S*$/));
    },
    inputs() {
      return [
        {
          name: 'name',
          translationPath: 'dialog.body.curves.name',
          clearable: true,
          rules: [this.inputRules.required, this.inputRules.noWhitespaces],
          dataType: {
            name: 'text',
            rules: [this.inputRules.required, this.inputRules.noWhitespaces],
          },
        },
        {
          name: 'description',
          translationPath: 'dialog.body.curves.description',
          clearable: true,
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'curveType',
          translationPath: 'dialog.body.curves.curveType',
          dataType: { name: 'select' },
          cols: '12',
          items: this.curveTypes,
          value: this.payload.curve_type,
          rules: [v => !!v || v === '0' || 'rules.required'],
        },
      ];
    },
    computedTitle() {
      return this.curve.id ? 'dialog.title.curveDetails' : 'dialog.title.addCurve';
    },
    isNew() {
      return !this.curve.id;
    },
    handleCurve() {
      return this.isNew ? this.handleAdd : this.handleEdit;
    },
    leftColumnTitle() {
      if (this.payload.curve_type === 'VOLUME') {
        return this.$i18n.t('dialog.body.curves.waterLevel');
      } else {
        return this.$i18n.t('dialog.body.curves.flow');
      }
    },
    rightColumnTitle() {
      if (this.payload.curve_type === 'HEADLOSS') {
        return this.$i18n.t('dialog.body.curves.headloss');
      } else if (this.payload.curve_type === 'VOLUME') {
        return this.$i18n.t('dialog.body.curves.volume');
      } else if (this.payload.curve_type === 'PUMP') {
        return this.$i18n.t('dialog.body.curves.pump');
      } else {
        return this.$i18n.t('dialog.body.curves.efficiency');
      }
    },
    isChart() {
      return !!this.chart;
    },
    isDataEmpty() {
      return this.chartData.length === 0;
    },
  },
  methods: {
    addCurve: call('epanet/addCurve'),
    editCurve: call('epanet/editCurve'),
    countReg(data) {
      let one = [];
      let two = [];
      let three = [];
      let x1 = data[0][0];
      let y1 = data[0][1];
      if (data.length === 1) {
        //Dla jednego punktu wyznaczamy dwa sąsiadujące
        one = [0, y1 * 1.33334];
        two = [x1, y1];
        three = [x1 * 2, 0];
      } else if (data.length === 3) {
        one = [x1, y1];
        two = [data[1][0], data[1][1]];
        three = [data[2][0], data[2][1]];
      } else {
        return;
      }
      //Regresja metodą najmniejszych kwadratów
      let poly = regression.polynomial([one, two, three], { precision: 6 });
      const [a, b, c] = poly.equation;
      //Obliczenie pomocnicznych punktów do narysowania wykresu
      let result = [];
      let n_vals = 30;
      const margin = (three[0] - one[0]) * 0.625;
      for (let i = 0; i <= n_vals; i++) {
        let x = ((three[0] - one[0] + one[0] + margin) / n_vals) * i;
        let y = a * Math.pow(x, 2) + b * x + c;
        if (y < 0) {
          continue;
        }
        result.push([x, y]);
      }
      return result;
    },
    computeChartData() {
      let length = this.chartData.length;
      if (length === 0) {
        this.chart?.destroy();
        return;
      }
      let sortedData = this.chartData.sort((a, b) => {
        return a[0] - b[0];
      });
      let data = [];
      let labels = [];
      let type = this.payload.curve_type;
      if (type !== 'PUMP') {
        if (length === 1) {
          this.isChartVisible = false;
        } else {
          for (let el of sortedData) {
            labels.push(el[0]);
            data.push(el[1]);
          }
          this.drawChart(labels, data);
        }
      } else {
        if (length === 2 || length > 4) {
          for (let el of sortedData) {
            labels.push(el[0]);
            data.push(el[1]);
          }
          this.drawChart(labels, data);
        } else if (length === 1 && sortedData[0][0] === 0) {
          this.isChartVisible = false;
        } else if ((length === 1 && sortedData[0][0] !== 0) || length === 3) {
          this.isChartVisible = true;
          let points = this.countReg(sortedData);
          for (let el of points) {
            labels.push(el[0]);
            data.push(el[1]);
          }
          this.drawChart(labels, data);
        }
      }
    },
    drawChart(labels, data) {
      const canvas = document.getElementById('curveChart');
      if (this.isChart) {
        this.chart.destroy();
      }
      let stepSize = null;
      stepSize = Math.max(...data) < 1 ? 0.1 : (stepSize = Math.max(...data) < 10 ? 1 : 5);
      this.chart = new Chart(canvas, {
        options: {
          responsive: true,
          maintainAspectRatio: true,
          legend: {
            display: false,
          },
          elements: {
            point: {
              radius: this.payload.curve_type === 'PUMP' ? 0 : 3,
            },
          },
          scales: {
            yAxes: [
              {
                ticks: {
                  callback: value => parseFloat(value).toFixed(1),
                  autoSkip: true,
                  stepSize: stepSize,
                },
                scaleLabel: {
                  display: true,
                  labelString: this.rightColumnTitle,
                },
              },
            ],
            xAxes: [
              {
                ticks: {
                  callback: value => parseFloat(value).toFixed(1),
                  autoSkip: true,
                  stepSize: Math.max(...labels) < 1 ? 0.1 : 5,
                  min: 0,
                },
                scaleLabel: {
                  display: true,
                  labelString: this.leftColumnTitle,
                },
              },
            ],
          },
        },
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            {
              data: data,
              fill: false,
              lineTension: 0,
              borderColor: '#2a579a',
              backgroundColor: '#2a579a',
            },
          ],
        },
      });
    },
    handlePointAdd() {
      this.chartData.push([this.newPoint.firstValue, this.newPoint.secondValue]);
      this.newPoint = {};
      this.computeChartData();
    },
    handlePointDelete(i) {
      this.chartData.splice(i, 1);
      this.computeChartData();
    },
    changeValue(value) {
      this.payload.curve_type = value;
      this.computeChartData();
    },
    onClose() {
      this.$emit('modalClosed');
    },
    resetPayload() {
      this.payload = undefined;
    },
    async handleAdd() {
      this.isLoading = true;
      const editedPayload = this.payload;
      editedPayload.data = this.chartData;
      if (this.nameAlreadyUsed) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.curveAlreadyExists'),
        });
        this.isLoading = false;
        return;
      }
      try {
        await this.addCurve(editedPayload);
      } finally {
        this.isLoading = false;
        this.$emit('modalClosed');
        this.computedIsVisible = false;
        this.resetPayload();
      }
    },
    async handleEdit() {
      this.isLoading = true;
      const editedPayload = this.payload;
      editedPayload.data = this.chartData;
      const data = { curve_id: this.curve.id, body: editedPayload };
      if (this.nameAlreadyUsed) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.curveAlreadyExists'),
        });
        this.isLoading = false;
        return;
      }
      try {
        await this.editCurve(data);
      } finally {
        this.isLoading = false;
        this.$emit('modalClosed');
        this.computedIsVisible = false;
        this.resetPayload();
      }
    },
    async init() {
      await this.$nextTick();
      this.chartData = cloneDeep(this.payload.data);
      this.computeChartData();
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.chart {
  @include border(left);
}
.tableRow {
  @include border(bottom);
}
.tableHeader {
  @include border(top);
  @include border(bottom);
  @include border(left);
}
</style>
